import { useEffect, useMemo } from "react";
// routes
// import { paths } from '@/routes/paths';
// locales

import { useLocales } from "@/locales";
import { useProductFeatures } from "@/hooks/useProductFeatures";
import { useGetEducatorDownloads } from "@/hooks/useGetEducatorDownloads";
// components
import SvgColor from "@/components/svg-color";
import DashboardIcon from "@/assets/icons/navbar/ic_dashboard.svg";
import HatIcon from "@/assets/icons/navbar/hat.svg";
import ExternalIcon from "@/assets/icons/navbar/ic_external.svg";
import LadderIcon from "@/assets/icons/navbar/ladder.svg";
import StarIcon from "@/assets/icons/navbar/star.svg";
import GroupIcon from "@/assets/icons/navbar/ic-solar_group.svg";
import CareerPlanFlagIcon from "@/assets/icons/navbar/career-plan-flag.svg";
import UserIcon from "@/assets/icons/navbar/ic-user-group.svg";
import ClimbersIcon from "@/assets/icons/navbar/ic-solar_climber.svg";

import GroupsIcon from "@/assets/icons/navbar/groups.svg";
import CourseIcon from "@/assets/icons/navbar/ic-course.svg";
import ReportsIcon from "@/assets/icons/navbar/ic_analytics.svg";
import WorkSitesIcon from "@/assets/icons/navbar/ic-solar_calendar-mark.svg";
import GearIcon from "@/assets/icons/navbar/ic_gear.svg";
import FileIcon from "@/assets/icons/navbar/ic_file.svg";
import SiteIcon from "@/assets/icons/navbar/ic_banking.svg";
import GetStartedIcon from "@/assets/icons/navbar/get-started.svg";
import { Stack, Typography } from "@mui/material";
import { useAwards } from "../../hooks/useCMSData";
import { useGetDistrictById } from "../../hooks/useGetDistrictById";
import { useCoursesContent } from "../../hooks/useCMSData";
import { useFeatureFlagEnabled } from "posthog-js/react";

// ----------------------------------------------------------------------

const filterVisible = (items) => {
  if (!items) return undefined;
  return items
    .filter((item) => item.visible === undefined || item.visible)
    .map((item) => ({
      ...item,
      items: filterVisible(item.items),
      children: filterVisible(item.children),
    }));
};

export function useNavData(currentUser) {
  const { t, onChangeLang } = useLocales();
  const { proskillPlans, matchesProductPlans, msPlans, wblPlans } =
    useProductFeatures();

  const { courses } = useCoursesContent();
  const { educatorDownloads } = useGetEducatorDownloads();
  let dashboardlink = "/";
  let data;

  const coachCareerPlanFlagValue = useFeatureFlagEnabled(
    "ms-career-plan-coach",
  );
  // Default to true while loading to prevent flickering on refresh
  const coachCareerPlanFlag = coachCareerPlanFlagValue !== false;

  const coursePlanFlagValue = useFeatureFlagEnabled("ms-course-plan");
  const coursePlanFlag = coursePlanFlagValue === true;

  const { awards } = useAwards();

  if (currentUser) {
    dashboardlink = `/${currentUser.role}/dashboard`;
  }

  // Use /ms/ routes for climbers that have MS plans
  if (currentUser?.role === "climber" && msPlans.length > 0) {
    dashboardlink = `/ms${dashboardlink}`;
  }

  const hasAwards = awards?.length > 0;

  const extractDistrictID = () => {
    const match = currentUser?.userPath?.match(/dis-[^#]+/);
    return match ? match[0] : null;
  };

  const { data: district } = useGetDistrictById(extractDistrictID());

  useEffect(() => {
    const districtNomenclature = district?.nomenclature;
    if (districtNomenclature !== undefined) {
      const lang = `en_${districtNomenclature}`;
      onChangeLang(lang);
    }
  }, [district?.nomenclature]);

  if (currentUser.role === "climber") {
    const isWbl = matchesProductPlans([
      "wbl-staff",
      "wbl-ux",
      "wbl-practicum",
      "wbl-practicum-staff",
    ]);
    data = useMemo(
      () => [
        {
          // Climber items
          items: [
            {
              title: "Dashboard",
              icon: (
                <SvgColor src={DashboardIcon} sx={{ width: 1, height: 1 }} />
              ),
              path: dashboardlink,
            },
            {
              title: "Hats",
              icon: <SvgColor src={HatIcon} sx={{ width: 1, height: 1 }} />,
              path: `${msPlans.length > 0 ? "/ms" : ""}/hats`,
              visible: !isWbl,
            },
            {
              title: "Career Climbs",
              icon: <SvgColor src={CourseIcon} sx={{ width: 1, height: 1 }} />,
              path: `${msPlans.length > 0 ? "/ms" : ""}/career-climbs`,
              visible: !isWbl,
            },
            {
              title: "Ladders",
              icon: <SvgColor src={LadderIcon} sx={{ width: 1, height: 1 }} />,
              path: `${msPlans.length > 0 ? "/ms" : ""}/ladders`,
              visible: !isWbl,
            },
            {
              title: "Courses",
              icon: <SvgColor src={GroupIcon} sx={{ width: 1, height: 1 }} />,
              path: `${msPlans.length > 0 ? "/ms" : ""}/courses`,
              visible: proskillPlans.length > 0 || wblPlans.length > 0,
            },
            {
              title: "Achievements",
              icon: <SvgColor src={StarIcon} sx={{ width: 1, height: 1 }} />,
              path: `${msPlans.length > 0 ? "/ms" : ""}/achievements`,
              visible: hasAwards,
            },
          ],
        },
      ],
      [t],
    );
  } else if (
    ["teacher", "schoolstaff", "districtadmin"].includes(currentUser.role)
  ) {
    const isMsStaff = matchesProductPlans(["ms-staff"]);
    const isWblStaff = matchesProductPlans([
      "wbl-staff",
      "wbl-ux",
      "wbl-practicum",
      "wbl-practicum-staff",
    ]);
    const isWfStaff = matchesProductPlans(["wf-staff", "launch-content"]);

    const isTeacher = currentUser.role === "teacher";
    const isSchoolStaff = currentUser.role === "schoolstaff";
    const isDistrictAdmin = currentUser.role === "districtadmin";

    const staffUserChildren = [
      {
        title: "Coaches",
        path: "users/coaches",
        visible: isSchoolStaff || isDistrictAdmin,
      },
      {
        title: "Staff",
        path: "users/staff",
        visible: isDistrictAdmin || (isSchoolStaff && currentUser.canAddPeers),
      },
      {
        title: "Administrators",
        path: "users/admins",
        visible: isDistrictAdmin,
      },
      {
        title: "Climbers",
        path: "users/climbers",
        visible: isTeacher || isSchoolStaff || isDistrictAdmin,
      },
    ];

    data = useMemo(
      () =>
        filterVisible([
          {
            items: [
              {
                title: "Dashboard",
                icon: (
                  <SvgColor src={DashboardIcon} sx={{ width: 1, height: 1 }} />
                ),
                path: dashboardlink,
              },
            ],
          },
          {
            subheader: isDistrictAdmin
              ? t("Group Management")
              : t("class_singular") + " Management",
            items: [
              {
                title: t("school_plural"),
                icon: <SvgColor src={SiteIcon} sx={{ width: 1, height: 1 }} />,
                path: "/sites",
                visible: !isTeacher,
              },
              {
                title: "Users",
                icon: <SvgColor src={UserIcon} sx={{ width: 1, height: 1 }} />,
                path: "/users",
                children: staffUserChildren,
                visible: !isTeacher,
              },

              {
                title: t("class_plural"),
                icon: (
                  <SvgColor src={GroupsIcon} sx={{ width: 1, height: 1 }} />
                ),
                path: "/groups",
                visible: isTeacher,
              },

              {
                title: "Climbers",
                icon: <SvgColor src={UserIcon} sx={{ width: 1, height: 1 }} />,
                path: "/users/climbers",
                visible: isTeacher,
              },

              {
                title: <span>Climber Plans</span>,
                icon: (
                  <SvgColor
                    src={CareerPlanFlagIcon}
                    sx={{ width: 1, height: 1 }}
                  />
                ),
                path: "/staff/career-course-planner",
                visible: isMsStaff && coachCareerPlanFlag && isTeacher,
              },
              {
                title: (
                  <Stack>
                    <span>Career Plans</span>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      coming soon
                    </Typography>
                  </Stack>
                ),
                icon: (
                  <SvgColor
                    src={CareerPlanFlagIcon}
                    sx={{ width: 1, height: 1 }}
                  />
                ),
                path: "/staff/career-course-planner-coming-soon",
                visible: isMsStaff && !coachCareerPlanFlag,
              },
              {
                title: (
                  <Stack>
                    <span>Reports</span>
                  </Stack>
                ),
                icon: (
                  <SvgColor src={ReportsIcon} sx={{ width: 1, height: 1 }} />
                ),
                path: "/reports/dashboard",
              },
              {
                visible: isTeacher && isWblStaff,
                title: (
                  <Stack>
                    <span>Site Visits</span>
                  </Stack>
                ),
                icon: (
                  <SvgColor src={WorkSitesIcon} sx={{ width: 1, height: 1 }} />
                ),
                path: "/staff/worksites",
              },
            ],
          },

          {
            subheader: "Coach Toolkit",
            items: [
              {
                title: "Get Started",
                icon: (
                  <SvgColor src={GetStartedIcon} sx={{ width: 1, height: 1 }} />
                ),
                path: "/educator-downloads/implementation-materials",
                visible: educatorDownloads.length > 0,
              },
              {
                title: "Lessons & Activities",
                icon: <SvgColor src={FileIcon} sx={{ width: 1, height: 1 }} />,
                path: "/educator-downloads/lessons",
                visible: educatorDownloads.length > 0,
              },
              {
                title: (
                  <div>
                    Climber Mode
                    <Typography sx={{ fontSize: 12 }}>
                      Middle School Only
                    </Typography>
                  </div>
                ),
                icon: (
                  <SvgColor src={ClimbersIcon} sx={{ width: 1, height: 1 }} />
                ),
                path: "/teacher/ms-climber-experience",
                visible: isMsStaff && isTeacher,
              },

              {
                visible: isWblStaff || isWfStaff,
                icon: <SvgColor src={HatIcon} sx={{ width: 1, height: 1 }} />,
                title: "Hats",
                path: "/climber-experience/hats",
              },

              {
                title: "Career Climbs",
                icon: (
                  <SvgColor src={CourseIcon} sx={{ width: 1, height: 1 }} />
                ),
                path: "/climber-experience/career-climbs",
                visible: isWblStaff || isWfStaff,
              },
              {
                title: "Courses",
                icon: <SvgColor src={GroupIcon} sx={{ width: 1, height: 1 }} />,
                path: "/climber-experience/courses",
                visible: (isWblStaff || isWfStaff) && courses.length > 0,
              },
              {
                visible: isWfStaff,
                title: "Help Center",
                icon: (
                  <SvgColor src={ExternalIcon} sx={{ width: 1, height: 1 }} />
                ),
                externalLink: true,
                path: "https://help.hatsandladders.com/",
              },
            ],
          },

          {
            subheader: t("Configuration"),
            visible: isDistrictAdmin,
            items: [
              {
                title: "Settings",
                icon: <SvgColor src={GearIcon} sx={{ width: 1, height: 1 }} />,
                path: "/settings",
              },
            ],
          },
        ]),
      [t],
    );
  }
  return data;
}
